// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useState, useMemo, Fragment } from 'react';

import {
  type PartCatalog,
  PART_TYPE_ASSEMBLY_ID,
  type Part,
  useEvolveDelete,
  type PartListPart,
} from '@evolvemep/api-ts';
import { Divider, Stack, InputLabel, Text, ActionIcon, Flex, Loader, Badge } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNil } from 'helpers/isNotNil';
import {
  CreateEditPartFields,
  type MoveToAssemblyEditorState,
  CreateEditPartForm,
} from 'modules/Shop/CatalogSetup/CreateEditPartFields';
import {
  type CreateEditPartFormType,
  PartEditorProvider,
  usePartEditor,
} from 'modules/Shop/CatalogSetup/PartEditor/usePartEditor';
import { useCatalogSetup } from 'modules/Shop/CatalogSetup/useCatalogSetup';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

type Props = {
  partCatalog: PartCatalog | undefined;
  partCategory: CategoryPath[number] | undefined;
  onPartSelected: (part: PartListPart) => void;
};

export const PART_NAME_MAX_LENGTH = 128;

/** Generates a new part name for a duplicated part by appending ` - copy` or ` - copy (n)` if necessary. */
const getCopiedPartName = (originalName: string) => {
  const endsWithCopyRegex = / - copy(?: \((\d+)\))?$/;
  const endsWithCopyMatch = originalName.match(endsWithCopyRegex);

  // If the original name already ends with ` - copy` or ` - copy (n)`, increment the number (starting at 2).
  if (endsWithCopyMatch) {
    const copyNumber = endsWithCopyMatch[1] ? parseInt(endsWithCopyMatch[1]) + 1 : 2;

    const appendix = ` - copy (${copyNumber})`;
    return originalName.slice(0, PART_NAME_MAX_LENGTH - appendix.length).replace(endsWithCopyRegex, appendix);
  }

  // Otherwise, just append " - copy".
  const appendix = ` - copy`;
  return `${originalName.slice(0, PART_NAME_MAX_LENGTH - appendix.length)} - copy`;
};

const DetailInfo = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack gap={4}>
    <InputLabel>{label}</InputLabel>
    {isNil(value) || typeof value === 'string' ? <Text>{value || '--'}</Text> : value}
  </Stack>
);

const PartDetailList = ({
  onClickEdit,
  onClickDuplicate,
  onClickDelete,
}: {
  onClickEdit: (e: true) => void;
  onClickDuplicate: (d: true) => void;
  onClickDelete: (d: true) => void;
}) => {
  const navigate = useEvolveNavigate('Catalog Setup');
  const { part, partCatalog, form } = usePartEditor();

  const attributes = useMemo(
    () =>
      (part ? [...part.globalPartAttributes, ...part.localPartAttributes] : []).toSorted((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [part],
  );

  if (isNil(part)) return <Loader />;

  const categoryPath = form.values.partCategory?.path ?? [];

  return (
    <>
      <Flex gap="md" justify="space-between" align="center" mb="md" h={36}>
        <Text fw="bold">Properties</Text>
        {part.editable && (
          <Flex gap="xs">
            <AgGridStyleTooltip
              label={part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Edit in Assembly Editor' : 'Edit part'}
              openDelay={0}
              withArrow
            >
              <ActionIcon
                variant={part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'gradient' : 'light'}
                onClick={() => {
                  if (part.partTypeId === PART_TYPE_ASSEMBLY_ID) {
                    void navigate(`/edit-catalog-item/${part.partId}`, {
                      additionalData: {
                        part,
                        partCatalog,
                      } as MoveToAssemblyEditorState,
                    });
                  } else {
                    onClickEdit(true);
                  }
                }}
              >
                <EvolveIcon icon="Edit" />
              </ActionIcon>
            </AgGridStyleTooltip>
            <AgGridStyleTooltip label="Duplicate" openDelay={0} withArrow>
              <ActionIcon variant="light" onClick={() => onClickDuplicate(true)}>
                <EvolveIcon icon="Copy" size="sm" />
              </ActionIcon>
            </AgGridStyleTooltip>
            <AgGridStyleTooltip label="Delete" openDelay={0} withArrow>
              <ActionIcon variant="light" onClick={() => onClickDelete(true)}>
                <EvolveIcon icon="Delete" size="sm" />
              </ActionIcon>
            </AgGridStyleTooltip>
          </Flex>
        )}
      </Flex>
      <Stack gap="lg" flex={1} style={{ overflowY: 'auto' }}>
        <DetailInfo label="Part Name" value={part.name} />
        <DetailInfo label="Description" value={part.description} />
        <DetailInfo
          label="Category Path"
          value={
            <Flex wrap="wrap" align="center" columnGap={5}>
              {categoryPath.map((d, i) => (
                <Fragment key={d}>
                  <Text c={i < categoryPath.length - 1 ? 'dimmed' : undefined}>{d}</Text>
                  {i < categoryPath.length - 1 && <EvolveIcon icon="ArrowRight" color="gray.5" size="xs" />}
                </Fragment>
              ))}
            </Flex>
          }
        />
        <DetailInfo label="Manufacturer" value={part.manufacturerName} />
        <DetailInfo label="Catalog #" value={part.manufacturerCatalogNumber} />
        <DetailInfo label="Unit of Measure" value={`${part.unitOfMeasureName} (${part.unitOfMeasureCode})`} />
        <Divider label="Attributes" />
        {attributes.length === 0 && (
          <Text c="dimmed" fz="sm">
            No attributes.
          </Text>
        )}
        {attributes.map((attribute) => (
          <Stack key={attribute.partAttributeDefinitionId} gap={4}>
            <InputLabel>
              <Flex align="center" gap={8}>
                {attribute.name}
                {attribute.global && (
                  <AgGridStyleTooltip label="Global attribute" withArrow openDelay={200}>
                    <EvolveIcon icon="External" color="gray" size={16} />
                  </AgGridStyleTooltip>
                )}
              </Flex>
            </InputLabel>
            <Flex wrap="wrap" gap="xs">
              {attribute.variants.map((variant) => (
                <Badge key={variant.partAttributeDefinitionVariantId} color="gray.2" fw={500} c="dark" tt="none">
                  {variant.name}
                </Badge>
              ))}
            </Flex>
          </Stack>
        ))}
      </Stack>
    </>
  );
};

const DuplicatePartModalForm = ({ onPartUpdated }: { onPartUpdated: (part: Part) => Promise<void> }) => {
  const { publishPart, form } = usePartEditor();
  const onSubmit = async (formValues: CreateEditPartFormType) => {
    const part = await publishPart({ formValues, duplicateAsNew: true });
    await onPartUpdated(part);
  };
  useFormModal(form, onSubmit);
  return <CreateEditPartFields duplicatePart />;
};

export const PartDetail = ({ partCatalog, partCategory, onPartSelected }: Props) => {
  const { selectedPart, editing, onEditingChanged, forceRefresh } = useCatalogSetup();

  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { apiCall: deletePartApiCall, loading: deleteRunning } = useEvolveDelete(`shop/partCatalog/part/:partId`);

  if (!selectedPart) {
    return (
      <Flex flex={1} justify="center" align="center">
        <Text fz="sm" c="dimmed">
          Select a part to view details.
        </Text>
      </Flex>
    );
  }

  return (
    <Stack w="100%" h="100%" style={{ overflow: 'hidden' }} pos="relative" gap={0}>
      <PartEditorProvider part={selectedPart} partCatalog={partCatalog} partCategory={partCategory}>
        {editing ? (
          <CreateEditPartForm
            includeAssemblyButton
            onPartUpdated={async (part) => {
              forceRefresh('categories');
              if (part.partCategoryId === partCategory?.partCategoryId) {
                forceRefresh('filters', 'parts');
              }
              onPartSelected(part);
            }}
            onClose={() => onEditingChanged(false)}
          />
        ) : (
          <PartDetailList
            onClickEdit={onEditingChanged}
            onClickDuplicate={setDuplicating}
            onClickDelete={setDeleting}
          />
        )}
      </PartEditorProvider>

      <WrappedModal
        title="Delete part"
        opened={deleting}
        onConfirm={() =>
          deletePartApiCall({ url: `shop/partCatalog/part/${selectedPart.partId}` }).then(() =>
            forceRefresh('parts', 'categories'),
          )
        }
        onClose={() => setDeleting(false)}
        loading={deleteRunning}
        confirmationText="Delete"
      >
        <Text>
          Are you sure you want to delete <b>{selectedPart.name}</b>?
        </Text>
      </WrappedModal>

      <FormModal
        title={`Duplicate ${selectedPart.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'assembly' : 'part'} ${selectedPart.name}`}
        opened={duplicating}
        onClose={() => setDuplicating(false)}
        bodyProps={{ mih: 350 }}
        confirmationText="Create"
        startDirty
        type="submit"
      >
        <PartEditorProvider
          overrideDefaultValues={{
            name: getCopiedPartName(selectedPart.name),
            partCategory: partCategory?.editable ? partCategory : null,
          }}
          part={selectedPart}
          partCatalog={partCatalog}
        >
          <DuplicatePartModalForm
            onPartUpdated={async (part) => {
              forceRefresh('categories');
              if (part.partCategoryId === partCategory?.partCategoryId) {
                forceRefresh('filters', 'parts');
              }
              onPartSelected(part);
            }}
          />
        </PartEditorProvider>
      </FormModal>
    </Stack>
  );
};
